var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
let FieldManager = class FieldManager extends LitElement {
    constructor() {
        super(...arguments);
        this.matchColField = {};
        this.basicTypes = [
            { label: msg('Text', { id: 'field.type.text', desc: 'Text type' }), value: '_string' },
            { label: msg('Number', { id: 'field.type.number', desc: 'Number type' }), value: '_number' },
            { label: msg('Date', { id: 'field.type.date', desc: 'Date type' }), value: '_date' },
            { label: msg('Boolean', { id: 'field.type.boolean', desc: 'Boolean type' }), value: '_boolean' },
            { label: msg('Ignore', { id: 'field.type.ignore', desc: 'Ignore type' }), value: '_ignore' },
        ];
    }
    updated(_changedProperties) {
        if (_changedProperties.has('dataFrame') ||
            _changedProperties.has('fields')) {
            this.matchColField = {};
            if (this.dataFrame && this.fields) {
                this.autoMatchFields();
            }
        }
    }
    autoMatchFields() {
        this.dataFrame.columns.forEach((col) => {
            const usedFields = Object.values(this.matchColField);
            const notUsedFields = this.fields.filter((f) => !usedFields.find((uf) => uf.name === f.name));
            const matchedField = notUsedFields.find((field) => field.name === col.name ||
                field.name === this.getName(col.name) ||
                field.alias.toLowerCase() === col.name.toLowerCase());
            if (matchedField) {
                console.log('Auto-matched column', col.name, 'to field', matchedField);
                this.matchColField[col.name] = matchedField;
            }
            else {
                console.log('No match for column', col.name, '- setting to basic string field');
                const detectedType = '_' + (col.type || 'string');
                this.matchColField[col.name] = {
                    name: '_' + this.getName(col.name),
                    alias: this.getAlias(col.name),
                    type: detectedType,
                    visible: true,
                    native: false,
                };
            }
        });
    }
    getAvailableTypes(field) {
        const fieldsToReturn = [];
        const usedFields = Object.values(this.matchColField);
        const notUsedFields = this.fields.filter((f) => !usedFields.find((uf) => uf.name === f.name));
        // Si pas un basic type
        if (!field.type?.startsWith('_')) {
            fieldsToReturn.push(field);
        }
        // Ajouts des fields qui ne sont pas déjà utilisés
        fieldsToReturn.push(...notUsedFields);
        // Ajout des basic types
        const types = fieldsToReturn.map((f) => ({
            label: f.alias,
            value: f.name,
        }));
        return [...types, ...this.basicTypes];
    }
    getFieldFromTypeOption(type) {
        if (type.startsWith('_')) {
            return null;
        }
        else {
            const field = this.fields.find((f) => f.name === type);
            if (field) {
                return field;
            }
            else {
                throw new Error(`Field with name ${type} not found`);
            }
        }
    }
    getAlias(colName) {
        return colName.charAt(0).toUpperCase() + colName.slice(1);
    }
    getName(colName) {
        let name = colName
            .normalize('NFD') // Décompose les caractères accentués en caractères de base + diacritiques
            .replace(/[\u0300-\u036f]/g, '') // Supprime les diacritiques (accents)
            .toLowerCase()
            .replace(/\s+/g, '_') // Remplace les espaces par des underscores
            .replace(/[^a-z0-9_]/g, ''); // Supprime les caractères non alphanumériques ou underscores
        if (name.startsWith('_')) {
            name = name.slice(1);
        }
        return name;
    }
    getFieldsMatch() {
        Object.values(this.matchColField).forEach((field) => {
            if (field.type?.startsWith('_')) {
                field.type = field.type.slice(1);
            }
            if (field.name.startsWith('_')) {
                field.name = field.name.slice(1);
            }
        });
        // Return all field if type != ignore
        const result = {};
        Object.entries(this.matchColField).forEach(([colName, field]) => {
            if (field.type !== 'ignore') {
                result[colName] = field;
            }
        });
        return result;
    }
    handleAliasChange(colName, newAlias) {
        const field = this.matchColField[colName];
        if (field) {
            field.alias = newAlias;
            this.matchColField = { ...this.matchColField, [colName]: field };
        }
    }
    handleTypeChange(colName, newType) {
        const newField = this.getFieldFromTypeOption(newType);
        // Si c'est un champ natif / existant
        if (newField) {
            this.matchColField = { ...this.matchColField, [colName]: newField };
        }
        else {
            // It's a basic type
            const type = newType;
            const newField = {
                name: '_' + this.getName(colName),
                alias: this.getAlias(colName),
                type: type, // Ensure 'ignore' is set correctly
                visible: type !== '_ignore',
                native: false,
            };
            this.matchColField = { ...this.matchColField, [colName]: newField };
        }
        this.requestUpdate();
    }
    render() {
        return html `
      <basic-card>
        <div class="field-manager">
          <div class="field-manager-header">
            <span>${msg('Column Name', { id: 'field.manager.columnName', desc: 'Column name header' })}</span>
            <span>${msg('Type', { id: 'field.manager.type', desc: 'Type header' })}</span>
            <span>${msg('Alias', { id: 'field.manager.alias', desc: 'Alias header' })}</span>
          </div>
          <div class="field-manager-body">
            ${Object.entries(this.matchColField).map(([colName, field]) => {
            return html ` <div class="field-manager-row">
                <div class="field-manager-cell">${colName}</div>
                <div class="field-manager-cell">
                  <select-input
                    ?special=${!field.type?.startsWith('_')}
                    variant="${field.native
                ? 'success'
                : !field.type?.startsWith('_')
                    ? 'warning'
                    : field.visible
                        ? 'secondary'
                        : 'danger'}"
                    .value=${!field.type?.startsWith('_')
                ? field.name
                : field.type}
                    .options=${this.getAvailableTypes(field)}
                    @select-change=${(e) => this.handleTypeChange(colName, e.detail.value)}
                  ></select-input>
                </div>
                <div class="field-manager-cell">
                  <input-text
                    variant="${field.native
                ? 'success'
                : !field.type?.startsWith('_')
                    ? 'warning'
                    : field.visible
                        ? 'secondary'
                        : 'danger'}"
                    .value=${field ? field.alias : colName}
                    @input-text=${(e) => this.handleAliasChange(colName, e.detail)}
                  ></input-text>
                </div>
              </div>`;
        })}
          </div>
        </div>
      </basic-card>
    `;
    }
};
FieldManager.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    basic-card {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden; /* Empêche le débordement global */
    }
    .field-manager {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .field-manager-header {
      display: grid;
      grid-template-columns: 1fr 2fr 2fr;
      gap: 16px;
      align-items: center;
      font-weight: bold;
      border-bottom: 2px solid #ddd;
      padding-bottom: 8px;
      background-color: white; /* Assure que le header reste visible */
      position: sticky; /* Rend le header fixe */
      top: 0; /* Fixe le header en haut du conteneur */
      z-index: 1; /* Assure que le header reste au-dessus des lignes */
    }
    .field-manager-body {
      overflow-y: auto; /* Ajoute une barre de défilement verticale */
      flex: 1; /* Permet au body de prendre tout l'espace restant */
    }
    .field-manager-row {
      display: grid;
      grid-template-columns: 1fr 2fr 2fr;
      gap: 16px;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #eee;
    }
    .field-manager-cell {
      display: flex;
      align-items: center;
      overflow: hidden; /* Empêche le dépassement */
      text-overflow: ellipsis; /* Ajoute "..." si le texte dépasse */
      white-space: nowrap; /* Empêche le retour à la ligne */
    }
    input-text {
      width: 100%;
    }
    select-input {
      width: 100%;
    }

    /* Styles personnalisés pour réduire la taille de la barre de défilement */
    .field-manager-body::-webkit-scrollbar {
      width: 6px; /* Largeur de la barre de défilement */
    }
    .field-manager-body::-webkit-scrollbar-track {
      background: #f1f1f1; /* Couleur de l'arrière-plan de la barre */
    }
    .field-manager-body::-webkit-scrollbar-thumb {
      background: #888; /* Couleur de la barre de défilement */
      border-radius: 4px; /* Arrondir les coins */
    }
    .field-manager-body::-webkit-scrollbar-thumb:hover {
      background: #555; /* Couleur de la barre au survol */
    }

    /* Pour les navigateurs non WebKit */
    .field-manager-body {
      scrollbar-width: thin; /* Réduit la largeur de la barre (Firefox) */
      scrollbar-color: #888 #f1f1f1; /* Couleur de la barre et de l'arrière-plan (Firefox) */
    }
  `;
__decorate([
    property({ type: Object })
], FieldManager.prototype, "dataFrame", void 0);
__decorate([
    property({ type: Array })
], FieldManager.prototype, "fields", void 0);
__decorate([
    state()
], FieldManager.prototype, "matchColField", void 0);
FieldManager = __decorate([
    localized(),
    customElement('field-manager')
], FieldManager);
export { FieldManager };
//# sourceMappingURL=FieldManager.js.map